import { Box } from "@mui/material";
import { useMemo } from "react";
import {
  LineChart as RechartsLineChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useTooltipAnimation } from "../../hooks/useTooltipAnimation";
import { LineChartAxes } from "./components/LineChartAxes";
import { LineChartGrid } from "./components/LineChartGrid";
import { LineChartLegend } from "./components/LineChartLegend";
import { LineChartLines } from "./components/LineChartLines";
import { LineChartTooltip } from "./components/LineChartTooltip";
import { useContainerWidth } from "./hooks/useContainerWidth";
import type { GapBridge } from "./utils";
import { buildGapBridges, hasValue } from "./utils";

const NUM_TICKS = 6;
const DEFAULT_MIN_Y_AXIS_VALUE = 5;
const DEFAULT_STROKE_WIDTH = 2;
const MIN_HIGHEST_Y_AXIS_VALUE = 10;
const BELOW_MIN_CLAMP_OFFSET = 0.001;
const Y_AXIS_HEADROOM_PERCENT = 110;

export const GRID_LINE_HEIGHT = 300;
export const PADDING_BOTTOM_Y_AXIS = 6;

export interface LineChartLegendConfig<T> {
  visibleSeries: Record<string, boolean>;
  onToggleSeries: (key: string, checked: boolean) => void;
  tooltips: Partial<Record<keyof T, string>>;
}

export interface LineChartProps<T> {
  data: T[];
  series: Array<{ key: keyof T; label: string; color: string }>;
  xKey: string & keyof T;
  xTickFormatter?: (value: string) => string;
  tooltipLabel?: (point: T) => string;
  minYAxisValue?: number;
  strokeWidth?: number;
  showLegend?: boolean;
  legend?: LineChartLegendConfig<T>;
  dashedGaps?: boolean;
  hideTooltipWithoutValue?: boolean;
}

export const LineChart = <T,>({
  data,
  series,
  xKey,
  xTickFormatter,
  tooltipLabel,
  minYAxisValue = DEFAULT_MIN_Y_AXIS_VALUE,
  strokeWidth = DEFAULT_STROKE_WIDTH,
  showLegend = false,
  legend,
  dashedGaps = false,
  hideTooltipWithoutValue = false,
}: LineChartProps<T>): React.ReactElement => {
  const isMobile = useIsMobile();
  const {
    ref: containerRef,
    chartHeight,
    margin,
    innerWidth,
  } = useContainerWidth<HTMLDivElement>(isMobile);

  useTooltipAnimation(containerRef);

  const filteredSeries = legend
    ? series.filter((s) => legend.visibleSeries[s.key as string])
    : series;

  const xData = useMemo(() => data.map((d) => String(d[xKey])), [data, xKey]);

  const clampedData = useMemo(() => {
    if (minYAxisValue === 0) {
      return data;
    }

    return data.map((d) => {
      const clamped = { ...d };
      for (const s of series) {
        // Null/undefined marks a period without data — keep it so the line can
        // skip it instead of clamping it onto the axis minimum.
        if (!hasValue(clamped[s.key])) continue;

        const val = Number(clamped[s.key]);
        if (Number.isFinite(val) && val < minYAxisValue) {
          (clamped as Record<string, unknown>)[s.key as string] =
            minYAxisValue - BELOW_MIN_CLAMP_OFFSET;
        }
      }

      return clamped;
    });
  }, [data, series, minYAxisValue]);

  const { rows: plotData, bridges } = useMemo(
    () =>
      dashedGaps
        ? buildGapBridges(
            clampedData,
            series.map((s) => String(s.key)),
          )
        : { rows: clampedData, bridges: [] as GapBridge[] },
    [clampedData, dashedGaps, series],
  );

  const allValues = clampedData.flatMap((d) =>
    filteredSeries
      .map((s) => d[s.key])
      .filter(hasValue)
      .map(Number),
  );

  const calculatedMax = Math.max(
    minYAxisValue,
    ...allValues.filter((v) => Number.isFinite(v)),
  );
  const yMax = Math.max(
    MIN_HIGHEST_Y_AXIS_VALUE,
    Math.ceil((calculatedMax * Y_AXIS_HEADROOM_PERCENT) / 100 / 10) * 10,
  );
  const yTicks = useMemo(() => {
    if (yMax <= 0) return [0];
    const step = (yMax - minYAxisValue) / (NUM_TICKS - 1);

    return Array.from({ length: NUM_TICKS }, (_, i) =>
      i === NUM_TICKS - 1 ? yMax : Math.round(minYAxisValue + step * i),
    );
  }, [yMax, minYAxisValue]);

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}
    >
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          position: "relative",
          "& .recharts-wrapper *:focus": { outline: "none" },
        }}
      >
        <ResponsiveContainer height={chartHeight} width="100%">
          <RechartsLineChart
            data={plotData}
            margin={margin}
            style={{ outline: "none" }}
          >
            <LineChartGrid />
            <LineChartAxes
              containerWidth={innerWidth}
              isMobile={isMobile}
              minYAxisValue={minYAxisValue}
              xData={xData}
              xKey={xKey}
              xTickFormatter={xTickFormatter}
              yMax={yMax}
              yTicks={yTicks}
            />
            <LineChartLines
              gapBridges={bridges}
              series={series}
              strokeWidth={strokeWidth}
              visibleSeries={legend?.visibleSeries}
            />
            <RechartsTooltip
              content={
                <LineChartTooltip
                  hideWithoutValue={hideTooltipWithoutValue}
                  minYAxisValue={minYAxisValue}
                  series={filteredSeries}
                  tooltipLabel={tooltipLabel}
                />
              }
              cursor={{
                strokeDasharray: `${GRID_LINE_HEIGHT} ${PADDING_BOTTOM_Y_AXIS}`,
              }}
              wrapperStyle={{ outline: "none" }}
            />
          </RechartsLineChart>
        </ResponsiveContainer>
      </Box>
      {showLegend && legend && (
        <LineChartLegend config={legend} series={series} />
      )}
    </Box>
  );
};
